(() => {
  if (window.HBPlantingPlanTracking) return;

  const config = window.HBPlantingPlanTrackingConfig || {};
  const storageKey = config.storageKey || "hbPlantingPlan:v1";
  const maxItems = 100;
  let previousItems = [];
  let pendingClear = false;
  let pendingCartItems = [];
  let syncReady = false;
  let syncTimer = null;

  const text = (value) => String(value == null ? "" : value).trim();
  const itemKey = (item) => text(item && item.productId);
  const quantity = (value) => Math.max(1, Math.min(99, Number.parseInt(value, 10) || 1));
  const nowId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  function storageGet(storageName, key) {
    try {
      const storage = window[storageName];
      return storage ? storage.getItem(key) || "" : "";
    } catch {
      return "";
    }
  }

  function storageSet(storageName, key, value) {
    try {
      const storage = window[storageName];
      if (!storage) return false;
      storage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }

  function normalizeItems(value) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value
      .filter((item) => item && item.productId && item.variantId && item.title)
      .filter((item) => {
        const key = itemKey(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, maxItems)
      .map((item) => {
        const addedAt = Number(item.addedAt) || Date.now();
        return {
          productId: text(item.productId),
          variantId: text(item.variantId),
          title: text(item.title).slice(0, 240),
          variantTitle: text(item.variantTitle).slice(0, 160),
          url: text(item.url).slice(0, 1000),
          image: text(item.image).slice(0, 1000),
          price: Math.max(0, Math.round(Number(item.price) || 0)),
          quantity: quantity(item.quantity),
          currency: text(item.currency || "GBP").slice(0, 8).toUpperCase() || "GBP",
          addedAt,
          updatedAt: Number(item.updatedAt) || addedAt,
        };
      });
  }

  function readItems() {
    try {
      return normalizeItems(JSON.parse(storageGet("localStorage", storageKey) || "[]"));
    } catch {
      return [];
    }
  }

  function writeItems(items) {
    const normalized = normalizeItems(items);
    const serialized = JSON.stringify(normalized);
    if (!storageSet("localStorage", storageKey, serialized)) return normalized;
    try {
      window.dispatchEvent(new StorageEvent("storage", {
        key: storageKey,
        newValue: serialized,
        storageArea: window.localStorage,
      }));
    } catch {
      window.dispatchEvent(new Event("storage"));
    }
    return normalized;
  }

  function planValue(items) {
    return items.reduce((total, item) => total + (Number(item.price) || 0) * quantity(item.quantity), 0);
  }

  function behaviourIdentity() {
    let shadow = {};
    try {
      shadow = JSON.parse(storageGet("localStorage", "hbBehaviourShadowV1") || "{}");
    } catch {
      shadow = {};
    }
    return {
      anonymousId: text(storageGet("localStorage", "hbBehaviourAnonId") || shadow.anonymousId),
      sessionId: text(storageGet("localStorage", "hbBehaviourSessionId") || shadow.sessionId),
      primaryInterest: text(shadow.primaryInterestCollection),
    };
  }

  function eventProperties(items, extra = {}) {
    return {
      plantingPlanItemCount: items.length,
      plantingPlanValue: planValue(items),
      plantingPlanValueCurrency: (items[0] && items[0].currency) || "GBP",
      accountLinked: Boolean(config.customerId && config.syncToken),
      pageType: text(config.pageType),
      ...extra,
    };
  }

  function sendEvent(eventName, item, properties = {}) {
    if (!config.trackingEndpoint) return;
    const items = readItems();
    const identity = behaviourIdentity();
    const payload = {
      eventName,
      eventId: `planting-plan:${eventName}:${nowId()}`,
      eventAt: new Date().toISOString(),
      anonymousId: identity.anonymousId,
      sessionId: identity.sessionId,
      shopifyCustomerId: config.customerId ? text(config.customerId) : "",
      productId: item ? text(item.productId) : "",
      productTitle: item ? text(item.title) : "",
      variantId: item ? text(item.variantId) : "",
      collectionId: text(config.collectionId),
      collectionTitle: text(config.collectionTitle || identity.primaryInterest),
      pageUrl: `${location.pathname}${location.search}`,
      referrerUrl: document.referrer || "",
      sourceFlow: "planting_plan",
      sourceCampaign: "planting_plan_engagement",
      properties: eventProperties(items, properties),
    };
    fetch(config.trackingEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  }

  async function syncRequest(action, items) {
    if (!config.syncEndpoint || !config.customerId || !config.syncToken) return null;
    const response = await fetch(config.syncEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,
        customerId: text(config.customerId),
        token: text(config.syncToken),
        ...(action === "save" ? { items: normalizeItems(items) } : {}),
      }),
      credentials: "omit",
      keepalive: action === "save",
    });
    if (!response.ok) throw new Error("Planting Plan account sync failed.");
    return response.json();
  }

  function sameItems(left, right) {
    return JSON.stringify(normalizeItems(left)) === JSON.stringify(normalizeItems(right));
  }

  function mergeItems(remoteItems, localItems) {
    const merged = new Map();
    normalizeItems(remoteItems).forEach((item) => merged.set(itemKey(item), item));
    normalizeItems(localItems).forEach((item) => merged.set(itemKey(item), item));
    return [...merged.values()]
      .sort((a, b) => Number(a.addedAt) - Number(b.addedAt))
      .slice(0, maxItems);
  }

  async function initializeSync() {
    previousItems = readItems();
    if (!config.customerId || !config.syncToken) return;
    try {
      const response = await syncRequest("load");
      const remoteItems = normalizeItems(response && response.plan && response.plan.items);
      const currentLocal = readItems();
      const merged = mergeItems(remoteItems, currentLocal);
      if (!sameItems(currentLocal, merged)) writeItems(merged);
      previousItems = merged;
      syncReady = true;
      if (!sameItems(remoteItems, merged)) await syncRequest("save", merged);
    } catch {
      syncReady = false;
    }
  }

  function scheduleSync(items) {
    if (!syncReady) return;
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncRequest("save", items).catch(() => {});
    }, 800);
  }

  function handlePlanChanged() {
    const nextItems = readItems();
    const before = new Map(previousItems.map((item) => [itemKey(item), item]));
    const after = new Map(nextItems.map((item) => [itemKey(item), item]));

    if (pendingClear && previousItems.length && !nextItems.length) {
      sendEvent("planting_plan_cleared", null, { removedItemCount: previousItems.length });
    } else {
      nextItems.forEach((item) => {
        const previous = before.get(itemKey(item));
        if (!previous) {
          sendEvent("planting_plan_item_saved", item);
        } else if (quantity(previous.quantity) !== quantity(item.quantity)) {
          sendEvent("planting_plan_quantity_changed", item, {
            previousQuantity: quantity(previous.quantity),
            nextQuantity: quantity(item.quantity),
          });
        }
      });
      previousItems.forEach((item) => {
        if (!after.has(itemKey(item))) sendEvent("planting_plan_item_removed", item);
      });
    }

    pendingClear = false;
    previousItems = nextItems;
    scheduleSync(nextItems);
  }

  function selectedPlanItems() {
    const selected = new Set(
      [...document.querySelectorAll("[data-hb-plan-select-item]:checked")]
        .map((input) => {
          const item = input.closest("[data-hb-plan-item]");
          return item && item.dataset.hbPlanItem;
        })
        .filter(Boolean),
    );
    return readItems().filter((item) => selected.has(itemKey(item)));
  }

  function handleClick(event) {
    const target = event.target && event.target.closest
      ? event.target
      : event.target && event.target.parentElement;
    if (!target) return;
    if (target.closest("[data-hb-plan-open]")) {
      const lastOpen = Number(storageGet("sessionStorage", "hbPlantingPlanLastTrackedOpen") || 0);
      if (Date.now() - lastOpen > 30 * 60 * 1000) {
        storageSet("sessionStorage", "hbPlantingPlanLastTrackedOpen", String(Date.now()));
        sendEvent("planting_plan_opened");
      }
    }
    if (target.closest("[data-hb-plan-clear]")) pendingClear = true;
    if (target.closest("[data-hb-plan-add-all]")) pendingCartItems = readItems();
    if (target.closest("[data-hb-plan-add-selected]")) pendingCartItems = selectedPlanItems();
  }

  function handleCartChanged() {
    if (!pendingCartItems.length) return;
    const items = pendingCartItems;
    pendingCartItems = [];
    sendEvent("planting_plan_added_to_cart", items[0], {
      addedItemCount: items.length,
      addedProductIds: items.map((item) => item.productId).slice(0, 25).join(","),
      addedPlanValue: planValue(items),
    });
  }

  function init() {
    previousItems = readItems();
    document.addEventListener("click", handleClick, true);
    document.addEventListener("hb:planting-plan:changed", handlePlanChanged);
    document.addEventListener("eurus:cart:items-changed", handleCartChanged);
    initializeSync();
  }

  window.HBPlantingPlanTracking = {
    readItems,
    sync: initializeSync,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
